import { z } from 'zod';

/**
 * DTOs for the OEM tray prediction microservice (`POST /predict`).
 *
 * This is a standalone service (not the periverse-api backend): callers resolve its
 * base URL from `VITE_OEM_TRAY_API_URL` and talk to it with a plain `fetch` (no
 * bearer token). The schemas below are the contract validated as data crosses into
 * the app; the request DTO is the body the app sends.
 */

/** `items[].status` — whether the model saw the slot filled. */
export const TrayPredictionItemStatusSchema = z.enum(['present', 'missing']);
export type TrayPredictionItemStatus = z.infer<
  typeof TrayPredictionItemStatusSchema
>;

/** Polygon vertex as `[y, x]` in raw input-image pixels, top-left origin. */
export const TrayPredictionMaskPointSchema = z.tuple([z.number(), z.number()]);
export type TrayPredictionMaskPoint = z.infer<
  typeof TrayPredictionMaskPointSchema
>;

/**
 * One predicted slot from `POST /predict`. The service may return `null` for an
 * unlocalizable slot's `mask`; the domain entity normalizes that to `[]`.
 */
export const TrayPredictionItemDtoSchema = z.object({
  /** Slot name. */
  name: z.string(),
  /** Tray/class identifier reported alongside the slot (optional). */
  classId: z.string().optional(),
  status: TrayPredictionItemStatusSchema,
  /** Model confidence in the status call, 0…1. */
  confidence: z.number(),
  /** Polygon outline as `[[y, x], ...]` vertices in pixels, or `null`. */
  mask: z.array(TrayPredictionMaskPointSchema).nullable(),
});
export type TrayPredictionItemDto = z.infer<typeof TrayPredictionItemDtoSchema>;

/** Response body from `POST /predict`. */
export const TrayPredictionResponseDtoSchema = z.object({
  /** Identified tray, or null if no tray was recognized (items empty). */
  tray: z.string().nullable(),
  items: z.array(TrayPredictionItemDtoSchema),
});
export type TrayPredictionResponseDto = z.infer<
  typeof TrayPredictionResponseDtoSchema
>;

/** Request body for `POST /predict`. */
export interface TrayPredictionRequestDto {
  /** base64-encoded JPEG/PNG photo (bare base64, no `data:` prefix). */
  image: string;
  /** Tray name; if omitted the service uses its auto-routing mechanism. */
  tray?: string;
}
